import React, { useState } from 'react';
import { Card, Row, Col, Badge, Progress, Tag, Tooltip, Modal, Descriptions, Spin, Alert, Empty } from 'antd';
import {
  CheckCircleOutlined,
  SyncOutlined,
  ToolOutlined,
  WarningOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { useWorkstationStatus, WorkstationStatus } from '../../../hooks/useManufacturingDashboard';

/** API may return DECIMAL fields as strings */
function formatPercentDisplay(value: unknown): string {
  const n = typeof value === 'number' && !Number.isNaN(value) ? value : Number(value);
  return Number.isFinite(n) ? n.toFixed(1) : '0.0';
}

interface WorkstationStatusMapProps {
  refreshInterval?: number;
}

const WorkstationStatusMap: React.FC<WorkstationStatusMapProps> = ({ refreshInterval }) => {
  const [selectedWorkstation, setSelectedWorkstation] = useState<WorkstationStatus | null>(null);
  const { data: workstations, isLoading, error } = useWorkstationStatus(refreshInterval);

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
      'Available': { color: '#52c41a', icon: <CheckCircleOutlined />, text: 'Available' },
      'In Use': { color: '#145A54', icon: <SyncOutlined spin />, text: 'In Use' },
      'Idle': { color: '#faad14', icon: <PauseCircleOutlined />, text: 'Idle' },
      'Maintenance': { color: '#fa8c16', icon: <ToolOutlined />, text: 'Maintenance' },
      'Breakdown': { color: '#ff4d4f', icon: <WarningOutlined />, text: 'Breakdown' },
    };
    return configs[status] || configs['Available'];
  };

  if (error) {
    return (
      <Card title="Workstation Status Map">
        <Alert
          message="Error Loading Workstation Status"
          description={error.message}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card title="Workstation Status Map">
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!workstations || workstations.length === 0) {
    return (
      <Card title="Workstation Status Map">
        <Empty description="No workstations found" />
      </Card>
    );
  }

  // Group by status for summary
  const statusSummary = workstations.reduce<Record<string, number>>((acc, ws) => {
    acc[ws.status] = (acc[ws.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <Card
        title="Workstation Status Map"
        extra={
          <div style={{ display: 'flex', gap: 16 }}>
            {Object.entries(statusSummary).map(([status, count]) => {
              const config = getStatusConfig(status);
              return (
                <Tag key={status} color={config.color}>
                  {config.text}: {count}
                </Tag>
              );
            })}
          </div>
        }
      >
        <Row gutter={[16, 16]}>
          {workstations.map((workstation) => {
            const statusConfig = getStatusConfig(workstation.status);
            const isActive = workstation.status === 'In Use';

            return (
              <Col key={workstation.workstationId} xs={24} sm={12} md={8} lg={6}>
                <Tooltip title="Click for details">
                  <Card
                    size="small"
                    hoverable
                    onClick={() => setSelectedWorkstation(workstation)}
                    style={{
                      borderLeft: `4px solid ${statusConfig.color}`,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong style={{ fontSize: 14 }}>
                          {workstation.workstationName}
                        </strong>
                        <Badge
                          status={isActive ? 'processing' : 'default'}
                          color={statusConfig.color}
                        />
                      </div>
                      <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                        {statusConfig.icon} {statusConfig.text}
                      </div>
                    </div>

                    {workstation.currentJob && (
                      <div style={{ marginBottom: 12, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>
                          Current Job
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>
                          {workstation.currentJob.workOrderNumber}
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                          {workstation.currentJob.operationName}
                        </div>
                        <Progress
                          percent={workstation.currentJob.progress}
                          size="small"
                          strokeColor="#145A54"
                          style={{ marginTop: 4 }}
                        />
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>Utilization</div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: '#145A54' }}>
                          {formatPercentDisplay(workstation.utilizationToday)}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>Efficiency</div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: '#52c41a' }}>
                          {formatPercentDisplay(workstation.efficiency)}%
                        </div>
                      </div>
                    </div>
                  </Card>
                </Tooltip>
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* Details Modal */}
      <Modal
        title={`Workstation Details - ${selectedWorkstation?.workstationName}`}
        open={!!selectedWorkstation}
        onCancel={() => setSelectedWorkstation(null)}
        footer={null}
        width={600}
      >
        {selectedWorkstation && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Status" span={2}>
              <Tag color={getStatusConfig(selectedWorkstation.status).color}>
                {getStatusConfig(selectedWorkstation.status).icon} {selectedWorkstation.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Utilization Today">
              {formatPercentDisplay(selectedWorkstation.utilizationToday)}%
            </Descriptions.Item>
            <Descriptions.Item label="Efficiency">
              {formatPercentDisplay(selectedWorkstation.efficiency)}%
            </Descriptions.Item>
            {selectedWorkstation.currentJob && (
              <>
                <Descriptions.Item label="Current Work Order" span={2}>
                  {selectedWorkstation.currentJob.workOrderNumber}
                </Descriptions.Item>
                <Descriptions.Item label="Operation" span={2}>
                  {selectedWorkstation.currentJob.operationName}
                </Descriptions.Item>
                <Descriptions.Item label="Progress" span={2}>
                  <Progress
                    percent={selectedWorkstation.currentJob.progress}
                    strokeColor="#145A54"
                  />
                </Descriptions.Item>
              </>
            )}
            {selectedWorkstation.assignedOperator && (
              <Descriptions.Item label="Assigned Operator" span={2}>
                {selectedWorkstation.assignedOperator.name}
              </Descriptions.Item>
            )}
            {selectedWorkstation.nextScheduledJob && (
              <>
                <Descriptions.Item label="Next Scheduled Job" span={2}>
                  {selectedWorkstation.nextScheduledJob.workOrderNumber}
                </Descriptions.Item>
                <Descriptions.Item label="Scheduled Time" span={2}>
                  {new Date(selectedWorkstation.nextScheduledJob.scheduledTime).toLocaleString()}
                </Descriptions.Item>
              </>
            )}
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default WorkstationStatusMap;
